import { useData } from "@hooks/useData";
import { ID } from "@datassential/platform-ui-lib";
import { iLTO, LTO } from "@models/LTO";
import { components } from "@apiSchema";
import http from "@core/fetch";
import { iPermissionFields, PermissionLevelEnum, PermissionSection } from "@models/PermissionSection";
import apiConfig from "@apiConfig";

type Data = {
    categories: Array<{
        id: ID;
        title: string;
    }>;
    itemsData: iPermissionFields & {
        data: {
            recentItems: Map<ID, iLTO[]>;
            topItems: Map<ID, iLTO[]>;
        },
    };
};

const DEFAULT_DATA: Data = {
    categories: [],
    itemsData: {
        permissionLevel: PermissionLevelEnum.Full,
        hasData: true,
        data: {
            recentItems: new Map(),
            topItems: new Map(),
        }
    },
}

const REQUEST_TYPE: keyof typeof apiConfig = 'get home all ltos';
type Response = components['schemas']['CategoryAllLtoModelViewModel'][];

async function getHomeLtos(): Promise<Data> {
    try {
        const response: Response = await http.get(REQUEST_TYPE);

        const permissionLevel = response
            .flatMap(i => [
                PermissionSection.getPermissionLevel(i.recentLtos?.permissionLevel),
                PermissionSection.getPermissionLevel(i.topLtos?.permissionLevel),
            ])
            .some(level => level === PermissionLevelEnum.Full) ? PermissionLevelEnum.Full : PermissionLevelEnum.Limited;

        return {
            categories: response.map(cat => ({
                id: cat.categoryId ?? 0,
                title: cat.categoryName ?? '',
            })),
            itemsData: {
                permissionLevel: permissionLevel,
                hasData: response[0]?.topLtos?.hasData ?? false,
                data: {
                    recentItems: new Map(response.map(cat => [
                        cat.categoryId ?? 0,
                        (cat.recentLtos?.data ?? []).map(i => new LTO({ apiModel: i })),
                    ])),
                    topItems: new Map(response.map(cat => [
                        cat.categoryId ?? 0,
                        (cat.topLtos?.data ?? []).map(i => new LTO({ apiModel: i })),
                    ])),
                }
            },
        };
    } catch (err) {
        console.error('Fetch error:', REQUEST_TYPE, err);
        return DEFAULT_DATA;
    }
}

export function useHomeLtos() {
    return useData(DEFAULT_DATA, getHomeLtos);
}